import { useCallback, useEffect, useMemo, useState } from 'react';

import { Divider, Stack } from '@mui/material';

import { useGetDatesQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectProject,
  selectRmEndDateTime,
  selectRmStartDateTime,
  setRmDateTimeRange,
} from 'state/workflowSlice';

import dayjs from 'dayjs';
import {
  dateTimeToString,
  dateToString,
  parseDate,
  parseDateTimeIgnoringTz,
} from 'features/common/datetimeUtils';
import { GenericDatePicker } from 'features/task_bar/DatePicker';
import { formatDateShort } from 'features/user_admin/common';

import StlConfirmButtons from '../StlConfirmButtons';
import DateTimePickerWrapper from './DateTimePickerWrapper';
import { TimeSelector } from './TimeSelector';
import {
  DEFAULT_HOURS,
  DEFAULT_TIME_WITH_MERIDIEM,
  HOURS,
  MAX_HOURS,
  calculateMaxHoursForDate,
  formatDateTimeToLocalLabel,
  formatTimeAmPm,
  hoursBetween,
  isSameDate,
  makeLocalDateFromStrings,
} from './utils';

import styles from './DateTimeSelector.module.css';

const WIDTH = '280px';
const HEIGHT = '284px';

type DateRange = { startDate: string; endDate: string };

export default function DateTimeSelector({
  disabled = false,
}: {
  disabled?: boolean;
}) {
  const dispatch = useAppDispatch();
  const project = useAppSelector(selectProject);
  const startDate = useAppSelector(selectRmStartDateTime);
  const endDate = useAppSelector(selectRmEndDateTime);

  const [tempRange, setTempRange] = useState<DateRange>({
    startDate: startDate,
    endDate: endDate,
  });

  const { data: datesInfo = [], isSuccess: datesInfoSuccess } =
    useGetDatesQuery(project);

  const [startHour, setStartHour] = useState<string>(
    startDate ? formatTimeAmPm(startDate) : DEFAULT_TIME_WITH_MERIDIEM,
  );
  const [open, setOpen] = useState<boolean>(false);
  const [endHour, setEndHour] = useState<string>(
    HOURS[parseDateTimeIgnoringTz(endDate).hour()],
  );
  const [hours, setHours] = useState<number>(
    hoursBetween(endDate, startDate) || DEFAULT_HOURS,
  );
  const [maxHours, setMaxHours] = useState(MAX_HOURS);
  const [initialValues, setInitialValues] = useState<
    { startDate: string; endDate: string } | undefined
  >(undefined);

  useEffect(() => {
    // keep state in sync with external updates (e.g. notification redirects).
    if (open || !startDate || !endDate) return;
    setTempRange({ startDate, endDate });
    setStartHour(formatTimeAmPm(startDate));
    setEndHour(formatTimeAmPm(endDate));
    setHours(hoursBetween(endDate, startDate) || DEFAULT_HOURS);
  }, [startDate, endDate, open]);

  useEffect(() => {
    if (open && !initialValues && startDate && endDate) {
      setInitialValues({ startDate, endDate });
    } else if (!open) setInitialValues(undefined);
  }, [open, startDate, endDate]);

  const calculateMaxHours = useCallback(
    (date: string) => {
      // while dates are loading, use MAX_HOURS.
      if (!datesInfoSuccess) {
        return MAX_HOURS;
      }
      return calculateMaxHoursForDate(date, datesInfo);
    },
    [datesInfo, datesInfoSuccess],
  );

  const updateDates = (date: string) => {
    const newStart = makeLocalDateFromStrings(date, startHour);
    const max = calculateMaxHours(newStart) || MAX_HOURS; // max hour is updated after start date is set
    setMaxHours(max);
    const newEnd = parseDateTimeIgnoringTz(newStart).add(
      Math.min(max, hours),
      'hour',
    );
    setTempRange({ startDate: newStart, endDate: dateTimeToString(newEnd) });
  };

  // update start date
  useEffect(() => {
    if (!startHour) return;
    setTempRange((prev) => {
      if (!prev.startDate) return prev;
      return {
        ...prev,
        startDate: makeLocalDateFromStrings(prev.startDate, startHour),
      };
    });
  }, [startHour]);

  useEffect(() => {
    if (!tempRange?.startDate) return;
    const maxH = calculateMaxHours(tempRange.startDate);
    setMaxHours(maxH);
    const endDateTime = parseDateTimeIgnoringTz(tempRange.startDate).add(
      Math.min(maxH, hours),
      'hour',
    );
    setTempRange((prev) => ({
      ...prev,
      endDate: dateTimeToString(endDateTime),
    }));
  }, [hours, tempRange?.startDate, calculateMaxHours]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleApply = () => {
    dispatch(
      setRmDateTimeRange({
        start: tempRange.startDate,
        end: tempRange.endDate,
      }),
    );
    handleClose();
  };

  const handleCancel = useCallback(() => {
    if (!initialValues) return;
    setTempRange({
      startDate: initialValues.startDate,
      endDate: initialValues.endDate,
    });
    // restore local state
    setStartHour(formatTimeAmPm(initialValues.startDate));
    setEndHour(formatTimeAmPm(initialValues.endDate));
    setHours(
      hoursBetween(initialValues.endDate, initialValues.startDate) ||
        DEFAULT_HOURS,
    );

    handleClose();
  }, [initialValues, dispatch, handleClose]);

  const getCalendarLabel = useMemo(() => {
    if (!tempRange?.startDate) return '';
    const now = dayjs();
    const startDayjs = parseDateTimeIgnoringTz(tempRange.startDate);
    if (isSameDate(startDayjs, now)) return 'Today';

    const yesterday = dayjs().subtract(1, 'day');

    if (isSameDate(startDayjs, yesterday)) return 'Yesterday';

    return formatDateShort(tempRange.startDate);
  }, [tempRange?.startDate]);

  const getButtonTitle = useCallback(() => {
    if (disabled) return formatDateShort(dateToString(dayjs()));
    if (!tempRange?.startDate || !tempRange?.endDate) return 'dd/mm/yyyy';
    return `${formatDateTimeToLocalLabel(tempRange?.startDate)} - ${formatDateTimeToLocalLabel(tempRange?.endDate)}`;
  }, [tempRange, disabled]);

  return (
    <GenericDatePicker
      narrow={false}
      disabled={disabled}
      setOpen={setOpen}
      open={open}
      getButtonTitle={getButtonTitle}
      enableDayArrows={false}
      targetDate={dateToString(parseDate(tempRange.startDate))}
      endDate={dateToString(parseDate(tempRange.endDate))}
      setNewTargetDate={updateDates}
      label={getCalendarLabel}
      onClose={handleApply}
      extras={
        <>
          <Divider
            className={styles.vertical_divider}
            orientation="vertical"
            variant="middle"
            flexItem
          />
          <Stack gap={1.5}>
            <DateTimePickerWrapper
              width={WIDTH}
              height={HEIGHT}
              title="Select time"
              component={
                <TimeSelector
                  maxHours={maxHours}
                  startHour={startHour}
                  setStartHour={setStartHour}
                  endHour={endHour}
                  setEndHour={setEndHour}
                  hours={hours}
                  setHours={setHours}
                />
              }
            />
            <StlConfirmButtons
              handleApply={handleApply}
              handleCancel={handleCancel}
              width={64}
            />
          </Stack>
        </>
      }
    />
  );
}
